/**
 * The "My views" panel's list: every saved view, grouped by where it lives.
 *
 * A view belongs to one point (or, for Capacity and RECs, one region), so the
 * panel groups by that — the point is what a reader navigates by. The point on
 * screen leads, so its views are one glance away; the rest follow in the order
 * they were last changed, which the server sends.
 *
 * Pure, so the grouping and the search are tested without a DOM.
 */

import { isoSlug } from "./paths";
import { SCOPE_LABELS, isRegionScope, viewHref } from "./view-links";

import type { MarketHub, MarketRegion, PointKindCode } from "@/types/market-intelligence";
import type { MarketView } from "@/types/market-views";

export interface MyViewsGroup {
  /** `point:<code>` or `region:<slug>`. */
  key: string;
  /** The point's or region's name. */
  title: string;
  /** The point's region code, beside its name. Absent for a region group. */
  region?: string;
  /** The point's kind, for its icon. Absent for a region group. */
  kind?: PointKindCode;
  /** Whether this is the point or region on screen. */
  current: boolean;
  views: Array<{ view: MarketView; href: string | null }>;
}

/**
 * The point or region a Market Intelligence url is on, as a group key — a
 * point's page at any depth (`…/hubs/pjm_west/forwards/strips`), or a
 * region's Capacity or RECs page. `null` anywhere else.
 */
export function groupKeyOf(pathname: string): string | null {
  const point = /\/hubs\/([^/?#]+)/.exec(pathname);
  if (point) return `point:${point[1]}`;
  const region = /\/regions\/([^/?#]+)\/(capacity|recs)(?:[/?#]|$)/.exec(pathname);
  return region ? `region:${region[1]}` : null;
}

export function groupMyViews(
  views: readonly MarketView[],
  hubs: readonly MarketHub[],
  regions: readonly MarketRegion[],
  currentKey: string | null,
  query: string,
): MyViewsGroup[] {
  const needle = query.trim().toLowerCase();
  const groups = new Map<string, MyViewsGroup>();

  for (const view of views) {
    const region = isRegionScope(view.scope);
    const key = `${region ? "region" : "point"}:${view.hub_code}`;
    const hub = region ? undefined : hubs.find((h) => h.code === view.hub_code);
    const regionCode = region
      ? (regions.find((r) => isoSlug(r.code) === view.hub_code)?.code ?? view.hub_code.toUpperCase())
      : undefined;

    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        title: region ? (regionCode ?? view.hub_code) : (hub?.name ?? view.hub_code),
        region: hub?.iso,
        kind: hub?.kind,
        current: key === currentKey,
        views: [],
      };
      groups.set(key, group);
    }

    if (needle) {
      const haystack = [view.name, SCOPE_LABELS[view.scope], group.title, group.region ?? ""]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(needle)) continue;
    }
    group.views.push({ view, href: viewHref(view, hub) });
  }

  const list = [...groups.values()].filter((g) => g.views.length > 0);
  // The server sends most recently changed first, so first-seen order is
  // recency; the point on screen jumps the queue.
  return [...list.filter((g) => g.current), ...list.filter((g) => !g.current)];
}
